import http from 'hub-http/clients/apiClient';

// Dashworks RPC API functions
export interface FetchAppParamsRequest {
  appId: number;
}

export interface FetchAppParamsResponse {
  authenticatorId: string;
  scope?: string;
  appName?: string;
  appId: number;
}

export interface StoreUserAppSecretRequest {
  portalId: number;
  appId: number;
  secretId: string;
  authenticatorId: string;
  userScope?: string;
}

export interface StoreUserAppSecretResponse {
  success: boolean;
}

export interface DashworksError {
  message: string;
  errorType: string;
}

export const fetchAppParams = async (
  appId: number
): Promise<FetchAppParamsResponse> => {
  try {
    // Make direct HTTP call to the CHIRP RPC endpoint
    const response = await http.post(
      '/chirp-frontend-app/v1/gateway/com.hubspot.dashworks.rpc.DashworksRpc/fetchAppParams',
      {
        data: {
          appId,
        },
      }
    );

    // Validate and convert the response
    if (response && typeof response === 'object' && !Array.isArray(response)) {
      // CHIRP responses are wrapped in a data field
      const chirpResponse = response as any;
      const result = chirpResponse.data as FetchAppParamsResponse;

      // Basic validation to ensure required fields are present
      if (
        result &&
        result.authenticatorId &&
        typeof result.appId === 'number'
      ) {
        return result;
      } else {
        throw new Error(
          `Invalid response format: missing required fields (authenticatorId or appId). Response: ${JSON.stringify(
            chirpResponse
          )}`
        );
      }
    } else {
      throw new Error(
        'Invalid response format from Dashworks RPC: expected object'
      );
    }
  } catch (error) {
    // Re-throw with more specific error message
    if (error instanceof Error) {
      throw new Error(
        `Failed to fetch app params from Dashworks RPC: ${error.message}`
      );
    } else {
      throw new Error(
        'Failed to fetch app params from Dashworks RPC: Unknown error'
      );
    }
  }
};

export const persistSecretId = async (
  appId: number,
  secretId: string,
  portalId: number,
  authenticatorId: string,
  userScope?: string
): Promise<StoreUserAppSecretResponse> => {
  try {
    const requestData: StoreUserAppSecretRequest = {
      portalId,
      appId,
      secretId,
      authenticatorId,
      userScope,
    };

    // Make direct HTTP call to the CHIRP RPC endpoint
    const response = await http.post(
      '/chirp-frontend-app/v1/gateway/com.hubspot.dashworks.rpc.DashworksRpc/storeUserAppSecret',
      {
        data: requestData,
      }
    );

    // Validate and convert the response
    if (response && typeof response === 'object' && !Array.isArray(response)) {
      // CHIRP responses are wrapped in a data field
      const chirpResponse = response as any;
      const result = chirpResponse.data as StoreUserAppSecretResponse;

      // Basic validation to ensure required fields are present
      if (result && typeof result.success === 'boolean') {
        return result;
      } else {
        throw new Error(
          `Invalid response format: missing success field. Response: ${JSON.stringify(
            chirpResponse
          )}`
        );
      }
    } else {
      throw new Error(
        'Invalid response format from Dashworks RPC: expected object'
      );
    }
  } catch (error) {
    // Re-throw with more specific error message
    if (error instanceof Error) {
      throw new Error(
        `Failed to persist secret ID to Dashworks RPC: ${error.message}`
      );
    } else {
      throw new Error(
        'Failed to persist secret ID to Dashworks RPC: Unknown error'
      );
    }
  }
};
